import { Button, Result, Spin } from "antd";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import MainLayout from "./layouts/mainLayout";
import ForgotPasswordPage from "./pages/account/forgot-password";
import LoginPage from "./pages/account/login";
import RegisterPage from "./pages/account/register";
import OTPValidationPage from "./pages/account/verify";
import Billing from "./pages/billing/Billing";
import PaymentCancel from "./pages/billing/paymentcancel";
import PaymentSuccess from "./pages/billing/paymentsuccess";
import ContentKB from "./pages/contentKB";
import Dashboard from "./pages/dashboard/Dashboard";
import Facebot from "./pages/facebot";
import Onboard from "./pages/onboard";
import Reports from "./pages/reports";
import SupportPage from './pages/support/support';
import { makeApiRequest } from "./shared/api";
import { FbContext } from "./shared/rbac/context";
import { fetchAllFaceBots } from "./shared/store/fbSlice";
import { fetchAllKb } from "./shared/store/kbSlice";

function RootRouter() {
  const dispatch = useDispatch();
  const authCxt = useContext(FbContext);
  const [faceBotCount, setFaceBotCount] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const { authenticated: isAuthenticated } = authCxt;

  useEffect(() => {
    const fetchData = async() => {
      if (isAuthenticated) {
        setLoading(true);
        try {
          await dispatch(fetchAllKb());
          await dispatch(fetchAllFaceBots());
          const response = await makeApiRequest("GET", "app/config");
          setFaceBotCount(response?.facebot_count ?? 0);
        } catch (error) {
          setFaceBotCount(0);
        } finally {
          setLoading(false);
        }
      } else {
        setFaceBotCount(0);
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, dispatch]);

  const isOnboardingComplete = faceBotCount > 0;

  if (loading || (isAuthenticated && faceBotCount === undefined)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  function getLoginElement() {
    if (isAuthenticated) {
      if (isOnboardingComplete) {
        return <Navigate to="/" replace />;
      } else {
        return <Navigate to="/onboard" replace />;
      }
    } else {
      return <LoginPage />;
    }
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Login Route */}
        <Route path="/login" element={getLoginElement()} />
        <Route index path="/register" element={<RegisterPage />} />
        <Route path="/verify" element={<OTPValidationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Onboarding Route */}
        <Route
          path="/onboard"
          element={
            isAuthenticated && !isOnboardingComplete
              ? <Onboard />
              : <Navigate to="/facebot" replace />
          }
        />

        {/* Main Authenticated App */}
        <Route
          path="/"
          element={
            isAuthenticated && isOnboardingComplete
              ? <MainLayout />
              : <Navigate to="/login" replace />
          }
        >
          <Route index path="/dashboard" element={<Dashboard />} />
          <Route path="/knowledge-base" element={<ContentKB />} />
          <Route path="/facebot" element={<Facebot />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/success" element={<PaymentSuccess /> } />
          <Route path="/cancel" element={<PaymentCancel />} />
          <Route path="/support" element={<SupportPage />} />

          {/* Reports Routes */}
          <Route path="reports">
            <Route path=":dashboardID" element={<Reports />} />
          </Route>

          {/* 404 Route within Dashboard Layout */}
          <Route path="*" element={
            <Result
              status="404"
              title="404"
              subTitle="Sorry, the page you visited does not exist."
              extra={<Button type="primary" onClick={() => window.history.back()}>Back Home</Button>}
            />
          } />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<h1>404 - Page Not Found</h1>} />
      </>
    )
  );

  return (
    <RouterProvider router={router}/>
  );
}

export default RootRouter;
